import { useState, useEffect, useRef } from 'react';

/**
 * 실제 뷰포트 감지 커스텀 훅
 * 가로축과 세로축 모두를 고려하여 요소가 실제로 화면에 보이는지 감지합니다.
 * 
 * @param {Object} options - 설정 객체
 * @param {number} options.threshold - 감지 임계값 (0~1, 기본값: 0.3)
 * @param {string} options.rootMargin - 루트 마진 (기본값: '0px')
 * @param {boolean} options.triggerOnce - 한 번만 트리거할지 여부 (기본값: true)
 * @returns {Array} [ref, isInView] - 요소 ref와 뷰포트 내 여부
 */
function useIsInView(options = {}) {
  const {
    threshold = 0.3,
    rootMargin = '0px',
    triggerOnce = true,
  } = options;
  
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const observerRef = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // 기존 observer 정리
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // 🔧 단순화된 뷰포트 감지 - 기본 intersectionRatio만 사용
    const checkRealViewport = (entry) => {
      const { intersectionRatio } = entry;
      
      // 복잡한 계산 제거하고 기본 Intersection Observer 결과만 사용
      return intersectionRatio >= threshold;
    };

    // Intersection Observer 생성
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const reallyInView = checkRealViewport(entry);
          
          if (reallyInView && (!triggerOnce || !hasTriggered)) {
            console.log('✅ Triggering animation - element is really in view');
            setIsInView(true);
            if (triggerOnce) {
              setHasTriggered(true);
            }
          } else if (!triggerOnce && !reallyInView) {
            console.log('❌ Hiding animation - element is out of view');
            setIsInView(false);
          }
        });
      },
      {
        threshold: 0.1, // 🔧 단일 threshold로 성능 최적화 (11개 배열 → 1개)
        rootMargin,
      }
    );

    observerRef.current.observe(element);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [threshold, rootMargin, triggerOnce, hasTriggered]);

  return [ref, isInView];
}

export default useIsInView; 